import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import {
  LogOutIcon,
  MenuIcon,
  XIcon,
  ShieldIcon,
  UserPlus,
  LayoutGrid,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const [isHeperumMobileMenuOpen, setIsHeperumMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1a' }}>
      {/* Heperum Header */}
      <header className="heperum-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Heperum Brand */}
            <Link to="/work-sessions">
              <img
                src="/heperum-logo.svg"
                alt="Heperum"
                className="h-12 brightness-0 invert cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>

            {/* Header Actions */}
            <div className="flex items-center gap-2">
              {/* Desktop buttons - hidden on mobile */}
              <div className="hidden md:flex md:items-center md:gap-4">
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="heperum-btn-gold"
                  >
                    <ShieldIcon className="h-4 w-4 mr-2" />
                    Admin
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link to="/explorer" className="heperum-btn-gold">
                    <LayoutGrid className="h-4 w-4 mr-2" />
                    Explorer
                  </Link>
                )}
                <a
                  href="mailto:?bcc=office@heperum.org&subject=Join%20us%20at%20Heperum&body=Hi.%0A%20%0AJoin%20me%20and%20others%20at%3A%20https%3A%2F%2Fwww.heperum.org%2F%0A%0ABR%2C%20"
                  className="heperum-btn-gold"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite a Friend
                </a>
                <button
                  onClick={logout}
                  className="heperum-btn-gold"
                >
                  <LogOutIcon className="h-4 w-4 mr-2" />
                  Log out
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 text-white hover:text-yellow-400 transition-colors touch-target"
                onClick={() => setIsHeperumMobileMenuOpen(!isHeperumMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isHeperumMobileMenuOpen ? (
                  <XIcon className="h-6 w-6" />
                ) : (
                  <MenuIcon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isHeperumMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-800">
          <div className="px-4 py-3 space-y-2">
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setIsHeperumMobileMenuOpen(false)}
                className="heperum-btn-gold w-full justify-center py-3"
              >
                <ShieldIcon className="h-4 w-4 mr-2" />
                Admin Panel
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link
                to="/explorer"
                onClick={() => setIsHeperumMobileMenuOpen(false)}
                className="heperum-btn-gold w-full justify-center py-3"
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Explorer
              </Link>
            )}
            <a
              href="mailto:?bcc=office@heperum.org&subject=Join%20us%20at%20Heperum&body=Hi.%0A%20%0AJoin%20me%20and%20others%20at%3A%20https%3A%2F%2Fwww.heperum.org%2F%0A%0ABR%2C%20"
              onClick={() => setIsHeperumMobileMenuOpen(false)}
              className="heperum-btn-gold w-full justify-center py-3"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite a Friend
            </a>
            <button
              onClick={() => {
                logout();
                setIsHeperumMobileMenuOpen(false);
              }}
              className="heperum-btn-gold w-full justify-center py-3"
            >
              <LogOutIcon className="h-4 w-4 mr-2" />
              Log out
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-4xl mx-auto py-8 px-4">
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <a
            href="mailto:info@heperum.org"
            className="text-sm"
            style={{ color: '#F4D03F' }}
          >
            Support & Contact
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
